import React from 'react';
import BaseEChart from './BaseEChart';
import { CHART_AXIS_LABEL_STYLE, CHART_COLORS } from './chartConstants';
import {
  computeCurrentProgressTooltipPosition,
  type TooltipPositionSize,
} from './currentProgressTooltip';
import { formatNumber, PLACEHOLDER } from '@utils/format';
import type { ChartOption } from '@app-types/chart';
import type { ProgressChartPoint } from '@app-types/performance';

interface AxisTooltipParam {
  seriesName?: string;
  data?: number | null;
  color?: string;
  axisValueLabel?: string;
  axisValue?: string;
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });

export interface CurrentProgressChartProps {
  max: number;
  unitSuffix: string;
  points: ProgressChartPoint[];
  height?: number;
}

const CURRENT_COLOR = '#337ebc';
const COMPANY_FORECAST_COLOR = '#aa5194';
const CONSENSUS_FORECAST_COLOR = '#00a79d';

const toSeriesData = (
  points: ProgressChartPoint[],
  key: keyof Pick<
    ProgressChartPoint,
    'previous' | 'current' | 'latestCompanyForecast' | 'consensusForecast'
  >,
) => points.map((point) => point[key]);

/**
 * Current progress status chart (Figma 3174:13760).
 *
 * The widget-library financial bar charts use ECharts grouped bars with DOM
 * legends. This follows that pattern, adding marker-only line series for the
 * company and consensus forecasts used by this Figma node.
 */
const CurrentProgressChart: React.FC<CurrentProgressChartProps> = ({
  max,
  unitSuffix,
  points,
  height = 205,
}) => {
  const yAxisInterval = max > 0 ? max / 5 : 100;
  const yAxisLabel = `${max}${unitSuffix}`;
  const gridLeft = Math.max(52, yAxisLabel.length * 8 + 12);

  const option: ChartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      appendToBody: true,
      className: 'esmart-chart-tooltip esmart-chart-tooltip--current-progress',
      // work_items_82: right-preferred, left-fallback, view-clamped so the
      // full tooltip stays visible on PC/SP instead of covering the chart or
      // running past an iframe edge.
      position: (point: unknown, _params: unknown, _dom: unknown, _rect: unknown, size: unknown) =>
        computeCurrentProgressTooltipPosition(
          point as [number, number],
          size as TooltipPositionSize,
        ),
      formatter: (params: unknown) => {
        const items = Array.isArray(params)
          ? (params as AxisTooltipParam[])
          : [params as AxisTooltipParam];
        if (items.length === 0) {
          return '';
        }
        const periodLabel = items[0]?.axisValueLabel ?? items[0]?.axisValue ?? '';
        const rows = items
          .map((item) => {
            const value = item.data;
            const displayValue =
              typeof value === 'number' && Number.isFinite(value)
                ? `${formatNumber(value)}${unitSuffix}`
                : PLACEHOLDER;
            const marker = item.color
              ? `<span class="esmart-chart-tooltip__marker" style="background:${escapeHtml(
                  item.color,
                )}"></span>`
              : '';
            return `<div class="esmart-chart-tooltip__row">${marker}<span class="esmart-chart-tooltip__name">${escapeHtml(
              item.seriesName ?? '',
            )}</span><span class="esmart-chart-tooltip__value">${displayValue}</span></div>`;
          })
          .join('');
        const title = periodLabel
          ? `<div class="esmart-chart-tooltip__title">${escapeHtml(periodLabel)}</div>`
          : '';
        return `${title}${rows}`;
      },
    },
    grid: {
      left: gridLeft,
      right: 0,
      top: 7,
      bottom: 24,
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      data: points.map((point) => point.label),
      axisTick: { show: false },
      axisLine: {
        show: true,
        lineStyle: { color: '#999999', width: 1 },
      },
      axisLabel: {
        ...(CHART_AXIS_LABEL_STYLE as any),
        lineHeight: 14,
        align: 'center',
        margin: 8,
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max,
      interval: yAxisInterval,
      axisTick: { show: false },
      axisLine: { show: false },
      splitLine: {
        show: true,
        lineStyle: { color: CHART_COLORS.grid, width: 1 },
      },
      axisLabel: {
        ...(CHART_AXIS_LABEL_STYLE as any),
        lineHeight: 14,
        margin: 8,
        // v20 refresh (§5.3): zero renders as '0B' (Billions) instead of '0'.
        formatter: (value: number) => (value === 0 ? '0B' : `${value}${unitSuffix}`),
      },
    },
    series: [
      {
        name: '前期',
        type: 'bar',
        data: toSeriesData(points, 'previous'),
        barWidth: 16,
        barGap: '12%',
        itemStyle: {
          color: CHART_COLORS.seriesPrimary,
          shadowColor: 'rgba(0, 0, 0, 0.2)',
          shadowOffsetY: 1,
        },
        emphasis: { disabled: true },
      },
      {
        name: '今期',
        type: 'bar',
        data: toSeriesData(points, 'current'),
        barWidth: 16,
        itemStyle: {
          color: CURRENT_COLOR,
          shadowColor: 'rgba(0, 0, 0, 0.2)',
          shadowOffsetY: 1,
        },
        emphasis: { disabled: true },
      },
      {
        name: '最新会社予想',
        type: 'line',
        data: toSeriesData(points, 'latestCompanyForecast'),
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: true,
        connectNulls: false,
        lineStyle: { opacity: 0 },
        itemStyle: { color: COMPANY_FORECAST_COLOR },
        emphasis: { disabled: true },
      },
      {
        name: 'コンセンサス予想',
        type: 'line',
        data: toSeriesData(points, 'consensusForecast'),
        symbol: 'triangle',
        symbolSize: 10,
        showSymbol: true,
        connectNulls: false,
        lineStyle: { opacity: 0 },
        itemStyle: {
          color: '#ffffff',
          borderColor: CONSENSUS_FORECAST_COLOR,
          borderWidth: 1,
        },
        emphasis: { disabled: true },
      },
    ],
  };

  return (
    <BaseEChart
      option={option}
      height={height}
      className="current-progress-chart"
      ariaLabel="今期進捗状況チャート"
      animate
    />
  );
};

export default CurrentProgressChart;
